import dayjs from "dayjs";
import {
    CLEANROOM_TYPES_CONFIG,
    get_locator_region_csp_from_identifier,
} from "../../common/utils/constants";
import { CLEANROOM_TYPES } from "./constants";
import { APPLICATION_IDS } from "../../srcV2/common/constants/constants";

const UNIT_ORDER = ["", "K", "M", "G", "T", "P"];

export const validateEmail = (email) => {
    return /^[\w.%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,64}$/.test(email);
};

export const validatePrintable = (input) => {
    var printable = /^[\x20-\x7E]*$/;
    return printable.test(input);
};

export const validateAccLocator = (account) => {
    return account.length > 0;
    // return /^[A-Za-z]{3}\d{5}$/.test(account);
};

export const isObjectEmpty = (objectName) => {
    for (let prop in objectName) {
        if (objectName.hasOwnProperty(prop)) {
            return false;
        }
    }
    return true;
};

export const isExists = (items, item) => items.indexOf(item) > -1;

export const resizeComponent = (
    inputRef,
    Streamlit,
    extraHeight = 0,
    timeout = 0,
) => {
    setTimeout(() => {
        if (inputRef?.current?.offsetHeight) {
            let height = inputRef.current.offsetHeight;
            Streamlit.setFrameHeight(height + extraHeight);
        } else {
            Streamlit.setFrameHeight();
        }
    }, timeout);
};

// To format date into mmm/dd/yyyy format
// Example: dayjs("6/29/2011 6:52:48 PM UTC").format('MMM DD, YYYY') returns 'Jun 30, 2011'
export const formatDate = (date) => {
    return dayjs(date).format("MMM DD, YYYY");
};

// To format timestamp into mmm/dd/yyyy format
export const getDateFromTimestamp = (timestamp) => {
    return formatDate(new Date(timestamp));
};

export const isSamoohaCleanroom = (cleanroomDetails) => {
    return (
        cleanroomDetails["type"] === CLEANROOM_TYPES.SAMOOHA ||
        !cleanroomDetails["type"]
    );
};

export const isAdsCleanroom = (cleanroom_type) => {
    return [CLEANROOM_TYPES.AMC, CLEANROOM_TYPES.ADH].includes(cleanroom_type);
};

export const isAMCCleanroom = (cleanroom_type) => {
    return cleanroom_type === CLEANROOM_TYPES.AMC;
};

export const isADHCleanroom = (cleanroom_type) => {
    return cleanroom_type === CLEANROOM_TYPES.ADH;
};

export const commaSeperatedNumber = (number) => {
    return Intl.NumberFormat("en-US").format(number);
};

export const deepCopy = (pData) => {
    return JSON.parse(JSON.stringify(pData));
};

export const reorder = (list, startIndex, endIndex) => {
    const result = Array.from(list);
    const [removed] = result.splice(startIndex, 1);
    result.splice(endIndex, 0, removed);

    return result;
};

export const getTableIcon = (tableName) => {
    return tableName.includes("AWS_")
        ? "static/images/icons/awsOnRA.svg"
        : tableName.includes("GCP_")
          ? "static/images/icons/install_google_cloud.svg"
          : "static/images/icons/sfOnRA.svg";
};

export const capitalizeFirstLetter = (word) => {
    if (undefined !== word && word.length > 0) {
        return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    }
    return word;
};

export const capitalizeFirstLettersInSentence = (sentence) => {
    const words = sentence.split(" ");
    const capitalizedWords = words.map((word) => capitalizeFirstLetter(word));
    const capitalizedSentence = capitalizedWords.join(" ");
    return capitalizedSentence;
};

/**
 * if label is a string, then return it
 * else if label is a JSX ie. react element then recursively reiterate to return the string
 * @param element
 * @returns string
 */
const extractTextContent = (element) => {
    if (typeof element === "string") {
        return element;
    } else if (element && element.props && element.props.children) {
        if (typeof element.props.children === "string") {
            return element.props.children;
        } else if (Array.isArray(element.props.children)) {
            return element.props.children.map(extractTextContent).join("");
        }
    }
    return "";
};

export const sortAlphabetically = (arr) => {
    return arr.sort((a, b) => {
        // Move null to the end
        if (a === null) return 1;
        // Move non-null above null
        if (b === null) return -1;

        const labelA = extractTextContent(a.label);
        const labelB = extractTextContent(b.label);

        const hasAllLabelA = labelA === "All";
        const hasAllLabelB = labelB === "All";

        // If one of them has 'All', prioritize it
        if (hasAllLabelA && !hasAllLabelB) return -1;
        if (!hasAllLabelA && hasAllLabelB) return 1;

        // If neither or both have 'All', perform normal alphabetical sorting
        if (labelA < labelB) return -1;
        if (labelA > labelB) return 1;
        return 0;
    });
};

export const checkPrivacyBudget = (privacySettings): [Boolean, string] => {
    const remainPrivacyBudget =
        privacySettings?.total_privacy_budget -
        privacySettings?.already_used_privacy_budget;
    let message = "";
    let canContinue = true;
    if (remainPrivacyBudget <= 0) {
        const renewalBackendDate =
            privacySettings?.privacy_budget_renewal_time_utc;
        const renewDate = dayjs(new Date(renewalBackendDate));
        const currentLocalDate = new Date();
        const currentDate = dayjs(currentLocalDate);
        let hours = renewDate.diff(currentDate, "hours");
        if (hours <= 0) {
            hours = 1;
        }
        canContinue = false;
        message = privacySettings.zero_budget_message;
        message = message.replace("##time_remaining##", hours.toString());
    }
    return [canContinue, message];
};

export const getNoiseRangeEstimate = (noiseMechanism, epsilon = 0.1) => {
    let result = -1;
    const b = 1 / epsilon;
    result = 2 * Math.sqrt(2) * b;

    return +Math.ceil(result);
};

export const dropdownOptions = (options = []) => {
    return options.map((item) => {
        return { label: item, value: item };
    });
};

/**
 *
 * @param num
 * @returns
 */
export const convertNumberToHumanReadable = (num) => {
    let magnitude = 0;
    while (Math.abs(num) >= 1000) {
        magnitude += 1;
        num /= 1000.0;
    }
    if (magnitude === 0) {
        return `${num}${UNIT_ORDER[magnitude]}`;
    } else {
        return `${num.toFixed(1)}${UNIT_ORDER[magnitude]}`;
    }
};

export const getProviderAccountLocator = (item) => {
    const { cleanroom_meta_type, meta_information, provider_account } = item;
    if (
        isSamoohaCleanroom(meta_information) ||
        isAdsCleanroom(cleanroom_meta_type)
    ) {
        return provider_account;
    }
    return CLEANROOM_TYPES_CONFIG[meta_information["type"]]["name"];
};

export const convertRampIdsToObjAndValidate = (arr, errorMsg) => {
    return arr.reduce(
        (acc, relation) => {
            if (acc.rampIds?.[relation.source]) {
                acc.errorMessage = errorMsg;
            } else if (!validateAccLocator(relation.source)) {
                acc.errorMessage = "Invalid snowflake account locator format";
            } else {
                acc.rampIds = {
                    ...acc.rampIds,
                    [relation.source.toUpperCase()]:
                        relation.destination.toUpperCase(),
                };
            }
            return acc;
        },
        { rampIds: {}, errorMessage: "" },
    );
};

export const convertRampIdsToArr = (obj) => {
    return Object.keys(obj)?.reduce((acc, key) => {
        acc.push({
            source: key,
            destination: obj[key],
        });
        return acc;
    }, []);
};

export const elipsisStyle = {
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
};

export const abbreviateNumber = (value, numbersAfterDecimal = 1) =>
    new Intl.NumberFormat("en-US", {
        maximumFractionDigits: numbersAfterDecimal,
        notation: "compact",
        compactDisplay: "short",
    }).format(value);

export const sanitizeCompactString = (value) => {
    if (value) {
        // convert a string with multiplier notations into a number
        // if value is "360.23K" or "20.13M" then convert it to to 360.2K | 20.1M
        const compactRegex = /^(\d*\.?\d+)([KMBT])$/;
        const match = value?.match(compactRegex);
        if (!match) {
            return value;
        }

        const numberPart = parseFloat(match[1]);
        const multiplierType = {
            K: 1000,
            M: 1000000,
            B: 1000000000,
            T: 1000000000000,
        };
        const multiplierValue = multiplierType[match[2]];

        if (!multiplierValue) {
            return value;
        }

        return numberPart * multiplierValue;
    } else return value;
};

export const parseFQTNC = (fqtnc) => {
    const fqrncLength = fqtnc.length;
    const returnArray = [];

    let inDoubleQuotes = false;
    let i = 0;
    let currentWordStartIndex = 0;

    while (i < fqrncLength) {
        const currentCharacter = fqtnc[i];
        if (currentCharacter === '"') {
            if (inDoubleQuotes) {
                if (i < fqrncLength - 1 && fqtnc[i + 1] === '"') {
                    i += 1;
                } else {
                    inDoubleQuotes = false;
                }
            } else {
                inDoubleQuotes = true;
            }
        } else if (currentCharacter === "." && !inDoubleQuotes) {
            returnArray.push(fqtnc.substring(currentWordStartIndex, i));
            currentWordStartIndex = i + 1;
        }
        i += 1;
    }

    returnArray.push(fqtnc.substring(currentWordStartIndex, i));
    return returnArray;
};

export const generateTestId = ({
    label = "",
    placeholder = "",
    customTestId = "",
}) => {
    let id =
        placeholder?.toLowerCase()?.split(" ")?.join("_") ||
        label?.toLowerCase()?.split(" ")?.join("_");

    if (customTestId) {
        const suffixId = customTestId.toLowerCase()?.split(" ")?.join("_");
        if (suffixId !== id) {
            id = id ? id + "_" + suffixId : suffixId;
        }
    }
    return id;
};

export const escapeDoubleQuotesForCSVDownload = (arrOfObjects) => {
    if (!Array.isArray(arrOfObjects)) {
        return [];
    }
    return arrOfObjects.map((obj) => {
        const newObj = {};
        for (let key in obj) {
            if (
                obj.hasOwnProperty(key) &&
                typeof obj[key] === "string" &&
                typeof key === "string" &&
                (obj[key]?.indexOf('"') || key?.indexOf('"'))
            ) {
                const value = obj[key]?.replace(/"/g, '""'); // replace all double quotes with two double quotes
                const newKey = key?.replace(/"/g, '""'); // replace double quotes in keys
                newObj[newKey] = value;
            } else {
                newObj[key] = obj[key];
            }
        }
        return newObj;
    });
};

export const isSameRegionAndCloud = ({ identifier, accountIdentifier }) => {
    const { region, csp } = get_locator_region_csp_from_identifier(identifier);
    const { region: account_region, csp: account_csp } =
        get_locator_region_csp_from_identifier(accountIdentifier);
    return (
        region.toUpperCase() === account_region.toUpperCase() &&
        csp.toUpperCase() === account_csp.toUpperCase()
    );
};

export const analysisWithScheduleEnabled = [
    "audience_overlap_analysis",
    "overlap_analysis",
];

export const getEnumKeyByValue = (enumObj, value) => {
    return Object.keys(enumObj).find((key) => enumObj[key] === value);
};

export const isTemplateDisabled = (applicationId) =>
    applicationId === APPLICATION_IDS.INVENTORY_FORECASTING ||
    applicationId === APPLICATION_IDS.LAST_TOUCH_ATTRIBUTION ||
    applicationId === APPLICATION_IDS.AUDIENCE_LOOKALIKE_MODELING;
